import { Award, BookOpen, MapPin, Scale, GraduationCap, ShieldCheck } from "lucide-react";

const LAWYER_PHOTO_1 = "./images/lawyer-photo-1.jpeg";
const OFFICE_SIGNBOARD = "./images/office-signboard.jpeg";
const INAUGURATION = "./images/inauguration.jpeg";

export default function AboutSection() {
  return (
    <section id="about" data-testid="about-section" className="py-28 bg-[#f7f3ea] relative overflow-hidden">
      {/* Subtle dot pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #b89968 1px, transparent 0)', backgroundSize: '48px 48px' }} />
      {/* Soft gold glow */}
      <div className="absolute top-40 -right-32 w-[480px] h-[480px] bg-[#b89968]/10 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 mb-5 px-5 py-2 rounded-full bg-[#b89968]/10 border border-[#b89968]/20">
            <Scale className="w-3.5 h-3.5 gold-accent" />
            <span className="text-xs font-semibold tracking-[0.2em] uppercase gold-accent">
              About the Advocate
            </span>
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight leading-tight text-[#0f1d3a] mb-3">
            Dedicated to Justice,<br />
            <span className="italic font-normal gold-accent">Committed to You</span>
          </h2>
          <div className="section-gold-line mx-auto mt-6" />
        </div>

        {/* Editorial Photo Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Photo Column — magazine stacked collage */}
          <div className="lg:col-span-5 relative">
            {/* Decorative gold accent block */}
            <div className="absolute -top-6 -left-6 w-32 h-32 border-2 border-[#b89968]/40 rounded-2xl hidden lg:block" />
            <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-[#b89968]/10 rounded-2xl hidden lg:block" />

            {/* Primary Portrait */}
            <div className="relative group">
              <div className="absolute -inset-2 bg-gradient-to-br from-[#b89968]/30 via-transparent to-[#b89968]/20 rounded-2xl blur-xl opacity-60 group-hover:opacity-100 transition-opacity duration-700" />
              <div className="relative overflow-hidden rounded-2xl shadow-2xl shadow-[#0f1d3a]/20 ring-1 ring-[#b89968]/20">
                <img
                  src={LAWYER_PHOTO_1}
                  alt="Adv. Akshay Dominic V"
                  className="w-full h-[520px] object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]"
                  data-testid="lawyer-photo"
                />
                {/* Caption overlay */}
                <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-[#0f1d3a]/85 via-[#0f1d3a]/40 to-transparent">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#b89968] mb-1.5 font-semibold">Founder</p>
                  <h3 className="font-heading text-2xl font-bold text-white leading-tight">Adv. Akshay Dominic V</h3>
                  <p className="text-xs text-white/70 mt-1.5 tracking-wider">BBA LLB (Hons) · Corpus Juris Law Chambers</p>
                </div>
              </div>
            </div>

            {/* Secondary Photo Strip — office signboard */}
            <div className="mt-5 grid grid-cols-5 gap-3">
              <div className="col-span-3 overflow-hidden rounded-xl shadow-lg shadow-[#0f1d3a]/10 group ring-1 ring-[#d9cdb4]">
                <img
                  src={OFFICE_SIGNBOARD}
                  alt="Corpus Juris Law Chambers Entrance"
                  className="w-full h-40 object-cover transition-transform duration-700 group-hover:scale-105"
                  data-testid="office-signboard"
                />
              </div>
              <div className="col-span-2 bg-[#0f1d3a] rounded-xl p-5 flex flex-col justify-center shadow-lg shadow-[#0f1d3a]/20">
                <p className="font-heading text-3xl font-bold gold-accent leading-none">Est.</p>
                <p className="font-heading text-2xl font-bold text-white mt-1">2020</p>
                <p className="text-[10px] text-white/50 mt-2 tracking-[0.15em] uppercase">Chavakkad · Kerala</p>
              </div>
            </div>
          </div>

          {/* Text + Details Column */}
          <div className="lg:col-span-7 flex flex-col lg:pl-8">
            {/* Pull-quote */}
            <p className="font-heading text-2xl lg:text-[1.65rem] leading-snug text-[#0f1d3a] mb-8 italic font-light">
              &ldquo;The law is not just a profession &mdash;
              <span className="gold-accent not-italic font-normal"> it is a promise </span>
              to stand with our clients at their most important moments.&rdquo;
            </p>

            <p className="text-base leading-relaxed text-[#3b4a6b] mb-5 font-light">
              A distinguished legal professional holding a <span className="font-medium text-[#0f1d3a]">BBA LLB (Hons)</span>,
              Adv. Akshay Dominic V combines sharp business acumen with rigorous legal craftsmanship.
              Based in Chavakkad, Thrissur, he founded
              <span className="font-medium text-[#0f1d3a]"> Corpus Juris Law Chambers </span>
              as a trusted destination for thoughtful, results-driven legal counsel across Kerala.
            </p>
            <p className="text-sm leading-relaxed text-[#6a7690] mb-10">
              With a deep grasp of Indian statute, precedent, and the nuances of the local legal
              landscape, Adv. Akshay delivers personalized counsel that is strategic, measured and
              unwaveringly client-first &mdash; whether steering complex litigation or negotiating
              decisive settlements.
            </p>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-4 mb-10">
              {[
                { num: "5+", label: "Years Practice" },
                { num: "500+", label: "Cases Handled" },
                { num: "9+", label: "Practice Areas" },
              ].map((s) => (
                <div key={s.label} className="glossy-stat relative text-center p-5 rounded-xl bg-white border border-[#d9cdb4] shadow-sm hover:shadow-lg hover:shadow-[#b89968]/15 hover:-translate-y-0.5 transition-all duration-300 overflow-hidden group">
                  <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[#b89968] to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <p className="font-heading text-3xl font-bold gold-accent">{s.num}</p>
                  <p className="text-[10px] text-[#8892ad] mt-2 uppercase tracking-[0.15em] font-medium">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Highlights */}
            <div className="space-y-3">
              {[
                { icon: GraduationCap, title: "BBA LLB (Hons)", desc: "Dual expertise in business and law for holistic counsel" },
                { icon: ShieldCheck, title: "Enrolled Advocate", desc: "Bar Council of Kerala · Practising across all courts in Kerala" },
                { icon: BookOpen, title: "Multi-Disciplinary Practice", desc: "Nine specialised areas from criminal defence to IP" },
                { icon: MapPin, title: "Chavakkad, Thrissur", desc: "Centrally located; serving clients across Kerala & beyond" },
              ].map(({ icon: Icon, title, desc }) => (
                <div key={title} className="flex items-center gap-4 p-4 rounded-xl border border-transparent hover:bg-white hover:border-[#d9cdb4] hover:shadow-sm transition-all group">
                  <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-full bg-[#0f1d3a] group-hover:bg-[#b89968] transition-colors duration-300">
                    <Icon className="w-4 h-4 text-white group-hover:text-[#0f1d3a]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#0f1d3a]">{title}</p>
                    <p className="text-xs text-[#8892ad] mt-0.5">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Secondary photo — full-width inauguration banner */}
        <div className="mt-24 relative group overflow-hidden rounded-2xl shadow-2xl shadow-[#0f1d3a]/10 ring-1 ring-[#d9cdb4]">
          <img
            src={INAUGURATION}
            alt="Corpus Juris Law Chambers Inauguration"
            className="w-full h-[340px] md:h-[460px] object-cover object-center transition-transform duration-[1500ms] ease-out group-hover:scale-[1.04]"
            data-testid="lawyer-photo-2"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0f1d3a]/85 via-[#0f1d3a]/50 to-[#0f1d3a]/10" />
          <div className="absolute inset-y-0 left-0 flex items-center p-8 md:p-14 max-w-xl">
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase gold-accent mb-3 font-bold">Our Beginning</p>
              <h3 className="font-heading text-3xl md:text-4xl font-bold text-white leading-tight mb-4">
                A firm built on <span className="italic gold-accent">trust</span>.
              </h3>
              <p className="text-sm md:text-base text-white/75 leading-relaxed">
                From the moment Corpus Juris opened its doors in Chavakkad, our promise has been
                simple &mdash; thoughtful counsel, transparent conversations, and unwavering
                advocacy for every client we represent.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
